//frontend/componets/demo/teamsCatalog.tsx
"use client";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { useTranslations, useMessages } from "next-intl";
import { useRouter } from "@/i18n/navigation";
import { useRouter as useAppRouter } from "next/navigation";
import { useAuth } from "@/components/auth/auth-context";


// ── Layer palette (closed graph — клиент видит только это) ──
const LAYER_CONFIG: Record<number, {
  label: string;
  bg: string;
  text: string;
  border: string;
  stripe: string;
}> = {
  1: { label: "L1 · Quick",      bg: "bg-violet-50", text: "text-violet-700", border: "border-violet-200", stripe: "bg-violet-400" },
  2: { label: "L2 · Standard",   bg: "bg-teal-50",   text: "text-teal-700",   border: "border-teal-200",   stripe: "bg-teal-500" },
  3: { label: "L3 · Pro",        bg: "bg-teal-50",   text: "text-teal-800",   border: "border-teal-300",   stripe: "bg-teal-600" },
  4: { label: "L4 · Advanced",   bg: "bg-blue-50",   text: "text-blue-700",   border: "border-blue-200",   stripe: "bg-blue-500" },
  5: { label: "L5 · Elite",      bg: "bg-amber-50",  text: "text-amber-700",  border: "border-amber-200",  stripe: "bg-amber-500" },
  6: { label: "L6 · Enterprise", bg: "bg-red-50",    text: "text-red-700",    border: "border-red-200",    stripe: "bg-red-500" },
};

const DOMAIN_LABEL: Record<string, string> = {
  education: "Education",
  math:      "Math",
  code:      "Code",
  science:   "Science",
  creative:  "Creative",
};

const DOMAIN_ICON: Record<string, string> = {
  education: "✦",
  math:      "∫",
  code:      "</>",
  science:   "⚛",
  creative:  "✎",
};


interface Team {
  slug: string;
  displayName: string;
  description: string;
  domain: string;
  layer: number;
  expectedMinutes: number;
  pricePerRunCents: number | null;
  isB2bOnly?: boolean;
  visibleForTiers: string[];
  minPriceMicroUsd?: number;
  maxPriceMicroUsd?: number;
  minSeconds?: number;
  maxSeconds?: number;
  isEnabled?: boolean;
  disabledReason?: 'coming_soon' | 'contact_us' | null;
}

//const ROUTER_URL = process.env.NEXT_PUBLIC_AI_ROUTER_URL ?? "https://router.salaria.top";
//const TEAMS_ENDPOINT = "/api/router/v1/teams?tier=F1_0";
const SKU_ENDPOINT = "/api/router/v1/sku";

export function TeamsCatalog({ showPrice = false }: { showPrice?: boolean } = {}) {
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<string>("all");
  const [fetchError, setFetchError] = useState<string | null>(null);
  const t = useTranslations("catalog");
  const messages = useMessages() as any;
  const router = useRouter();
  const { isAuthenticated, user } = useAuth();
  const appRouter = useAppRouter(); // для маршрутов вне [locale]: /chat, /shop

  // Гейт-логика по клику на карточку команды:
  //   a) залогинен + tier OK → /chat?team=<slug>
  //   b) не залогинен        → /signin?next=/chat?team=<slug>
  //   c) залогинен + tier недостаточен → /shop?team=<slug>
  const handleTeamClick = (team: Team) => {
  // contact_us → открываем mailto
    if (team.disabledReason === 'contact_us') {
      window.location.href = `mailto:sku@layvers.com?subject=${encodeURIComponent(team.displayName)}`;
      return;
    }
    // coming_soon → пускаем на /teams/:slug читать описание
    if (team.disabledReason === 'coming_soon') {
      router.push(`/teams/${team.slug}` as any);
      return;
    }
    // isEnabled === false без причины → блок
    if (team.isEnabled === false) return;

    const target = `/chat?team=${team.slug}`;
    if (!isAuthenticated) {
      router.push(`/teams/${team.slug}` as any);
      return;
    }
    const userTier = user?.tier ?? "F1_0";
    const allowed = team.visibleForTiers?.includes(userTier);
    if (allowed) {
      appRouter.push(target);
    } else {
      appRouter.push('/shop/deposit');
    }
  };

  const tr = (key: string, fallback: string) => {
    const parts = key.split(".");
    let cur: any = messages?.catalog;
    for (const p of parts) {
      if (cur == null || typeof cur !== "object") return fallback;
      cur = cur[p];
    }
    return typeof cur === "string" ? cur : fallback;
  };

 useEffect(() => {
    fetch(SKU_ENDPOINT, { credentials: "omit" })
      .then(async (r) => {
        if (!r.ok) throw new Error(`sku HTTP ${r.status}`);
        return r.json();
      })
      .then((data) => {
        const items: Team[] = Array.isArray(data) ? data : data.items ?? [];
        if (items.length === 0) throw new Error("sku empty");
        setTeams(items);
        setFetchError(null);
      })
      .catch((e) => {
        console.warn("[TeamsCatalog] fetch failed:", e);
        setFetchError(String(e?.message ?? e));
        setTeams([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const domains = ["all", "education", "math", "code", "science", "creative"];

  const visible = filter === "all"
    ? teams
    : teams.filter((t) => t.domain === filter);

  return (
    <div className="w-full">
      {/* ── Filter tabs ── */}
      <div className="flex flex-wrap gap-1 mb-5">
        {domains.map((d) => (
          <button
            key={d}
            onClick={() => setFilter(d)}
            className={cn(
              "px-3 py-1.5 rounded-lg text-[12px] font-medium transition-colors capitalize",
              filter === d
                ? "bg-slate-900 text-white"
                : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"
            )}
          >
            {d === "all" ? `${tr("filterAll", "All")} (${teams.length})` : tr(`domains.${d}`, DOMAIN_LABEL[d] ?? d)}
          </button>
        ))}
      </div>

      {/* ── Grid: 1 → 2 → 3 → 4 columns ── */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-[200px] rounded-2xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {visible.map((team) => {
            const layer = LAYER_CONFIG[team.layer] ?? LAYER_CONFIG[1];
                        const price = (() => {
              const minU = team.minPriceMicroUsd ?? 0;
              const maxU = team.maxPriceMicroUsd ?? 0;
              if (minU === 0 && maxU === 0) {
                return "Contact";
              }
              const fmt = (u: number) => {
                const usd = u / 1_000_000;
                if (usd < 0.01) return `$${usd.toFixed(4)}`;
                if (usd < 1)    return `$${usd.toFixed(3)}`;
                return `$${usd.toFixed(2)}`;
              };
              return minU === maxU ? fmt(minU) : `${fmt(minU)} — ${fmt(maxU)}`;
            })();
            const time = (() => {
              const minS = team.minSeconds ?? 0;
              const maxS = team.maxSeconds ?? 0;
              if (minS === 0 && maxS === 0) {
                return team.expectedMinutes < 2
                  ? `~${Math.max(team.expectedMinutes * 60, 30)}s`
                  : `~${team.expectedMinutes}min`;
              }
              const fmt = (s: number) => s < 60 ? `${s}s` : `${Math.round(s / 60)}min`;
              return minS === maxS ? fmt(minS) : `${fmt(minS)} — ${fmt(maxS)}`;
            })();

            return (
             <button
                key={team.slug}
                onClick={() => handleTeamClick(team)}
                disabled={team.isEnabled === false && !team.disabledReason}
                className="group flex flex-col rounded-2xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-md transition-all overflow-hidden min-h-[220px] text-left w-full disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:shadow-none disabled:hover:border-slate-200"
              >
                {/* Accent stripe by layer */}
                <div className={cn("h-1.5", layer.stripe)} />

                <div className="flex flex-col gap-4 p-6 flex-1">
                  {/* Top row: domain + layer pill */}
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                      <span className="mr-1.5 text-base">{DOMAIN_ICON[team.domain]}</span>
                      {tr(`domains.${team.domain}`, DOMAIN_LABEL[team.domain] ?? team.domain)}
                    </span>
                    <span className={cn(
                      "text-[11px] font-semibold px-2.5 py-1 rounded-full border",
                      layer.bg, layer.text, layer.border
                    )}>
                      {tr(`layers.${team.layer}`, layer.label)}
                    </span>
                  </div>

                  {/* Name + description */}
                  <div className="flex-1">
                    <p className="text-[17px] font-semibold text-slate-900 leading-snug mb-1.5">
                      {tr(`skus.${team.slug}.name`, team.displayName)}
                    </p>
                    <p className="text-[14px] text-slate-500 leading-relaxed line-clamp-2">
                      {tr(`skus.${team.slug}.desc`, team.description)}
                    </p>
                  </div>

                  {/* Price + time */}
                  <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                    {showPrice && (
                      <>
                        <span className="text-[15px] font-semibold text-slate-900">
                          {price === "Contact" ? tr("contactUs", "Contact us") : price}
                        </span>
                        <span className="text-slate-300">·</span>
                      </>
                    )}
                    <span className="text-[12px] text-slate-400">{time}</span>
                    {team.isEnabled === false && !team.disabledReason && (
                      <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-red-600">
                        <span className="w-2 h-2 rounded-full bg-red-500" />
                        {tr("unavailable", "Unavailable")}
                      </span>
                    )}
                    {team.disabledReason === 'coming_soon' && (
                      <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-violet-600">
                        <span className="w-2 h-2 rounded-full bg-violet-400" />
                        {tr("comingSoon", "Coming Soon")}
                      </span>
                    )}
                    {team.disabledReason === 'contact_us' && (
                      <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-medium text-blue-600">
                        <span className="w-2 h-2 rounded-full bg-blue-400" />
                        {tr("contactUsEmail", "Contact Us")}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        
      )}
    </div>
  );
}